// ManageUsers.jsx
import { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Tag,
  Input,
  message,
  Select,
  Card,
  Space,
  Typography,
  Badge,
  Avatar,
  Pagination,
  Spin,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  PoweroffOutlined,
} from "@ant-design/icons";
import { Popconfirm } from "antd";
import API from "../../services/api";
import { useNavigate } from "react-router-dom";
import "./ManageUsers.css";

const { Option } = Select;
const { Title, Text } = Typography;

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function ManageUsers() {
  const navigate = useNavigate();
  const width = useWindowWidth();

  // Breakpoint flags
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;
  const isMediumDesktop = width >= 1024 && width < 1310;
  const useCardLayout = width < 1024;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [postFilter, setPostFilter] = useState("all");
  const [counts, setCounts] = useState({ active: 0, inactive: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(
    async (pageNum, searchText, status, posts) => {
      try {
        setLoading(true);
        const query = new URLSearchParams({
          page: pageNum,
          limit,
          search: searchText,
        });
        if (status !== "all") query.append("status", status);
        if (posts !== "all") {
          query.append("hasPosts", String(posts));
        }

        const { data } = await API.get(`/users/all-users?${query}`);
        setUsers(data.users);
        setTotal(data.total);
        setPage(data.page);
        setCounts(data.counts || { active: 0, inactive: 0 });
      } catch (error) {
        message.error(
          error.response?.data?.message || "Unable to load user data"
        );
      } finally {
        setLoading(false);
      }
    },
    [limit]
  );

  useEffect(() => {
    fetchUsers(page, debouncedSearch, statusFilter, postFilter);
  }, [page, debouncedSearch, statusFilter, postFilter, fetchUsers]);

  const handleToggle = async (id) => {
    try {
      await API.patch(`/users/toggle-status/${id}`);
      message.success("Account status updated successfully");
      fetchUsers(page, debouncedSearch, statusFilter, postFilter);
    } catch (error) {
      message.error(
        error.response?.data?.message || "Failed to update account status"
      );
    }
  };

  const formatLastLogin = (date) => {
    if (!date || isNaN(new Date(date))) return null;
    const d = new Date(date);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    let relative =
      mins < 1
        ? "Just now"
        : mins < 60
          ? `${mins}m ago`
          : mins < 1440
            ? `${Math.floor(mins / 60)}h ago`
            : `${Math.floor(mins / 1440)}d ago`;
    return { relative, exact: d.toLocaleString() };
  };

  const StatusTag = ({ isActive }) =>
    isActive ? (
      <Tag
        icon={<CheckCircleOutlined />}
        color="success"
        className="status-tag status-active"
      >
        Active
      </Tag>
    ) : (
      <Tag
        icon={<CloseCircleOutlined />}
        color="error"
        className="status-tag status-inactive"
      >
        Suspended
      </Tag>
    );

  const ToggleButton = ({ record, block = false }) => (
    <Popconfirm
      title={`${record.isActive ? "Deactivate" : "Activate"} this account?`}
      onConfirm={() => handleToggle(record._id)}
      okText="Yes"
      cancelText="No"
    >
      <Button
        size="small"
        block={block}
        icon={<PoweroffOutlined />}
        className={record.isActive ? "action-btn-danger" : "action-btn-success"}
      >
        {record.isActive ? "Deactivate" : "Activate"}
      </Button>
    </Popconfirm>
  );

  // ─── FULL DESKTOP COLUMNS (≥ 1310px) ─────────────────────────────
  const fullColumns = [
    {
      title: "Account",
      dataIndex: "name",
      render: (name, record) => (
        <Space className="user-cell">
          <Avatar
            icon={<UserOutlined />}
            className="user-avatar"
            style={{
              backgroundColor: record.isActive ? "#10b981" : "#94a3b8",
            }}
          />
          <div className="user-info">
            <Text className="user-name">{name}</Text>
            <Text className="user-email">{record.email}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Access Level",
      dataIndex: "role",
      render: (role) => (
        <Badge
          className="role-badge"
          color={role === "admin" ? "#6366f1" : "#64748b"}
          text={role === "admin" ? "Administrator" : "Standard User"}
        />
      ),
    },
    {
      title: "Account Status",
      render: (_, record) => (
        <div className="status-container">
          <StatusTag isActive={record.isActive} />
        </div>
      ),
    },
    {
      title: "Recent Activity",
      dataIndex: "lastLoginAt",
      render: (date) => {
        const info = formatLastLogin(date);
        if (!info)
          return <Text className="text-muted">No activity recorded</Text>;
        return (
          <div className="login-info">
            <Text className="login-relative">{info.relative}</Text>
            <Text className="login-exact">{info.exact}</Text>
          </div>
        );
      },
    },
    {
      title: "Content",
      align: "center",
      render: (_, record) =>
        record.hasPosts ? (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            className="action-link"
            onClick={() => navigate(`/admin/user-posts/${record._id}`)}
          >
            View Content
          </Button>
        ) : (
          <Text className="text-muted">No content</Text>
        ),
    },
    {
      title: "Management",
      align: "center",
      render: (_, record) => <ToggleButton record={record} />,
    },
  ];

  // ─── MEDIUM DESKTOP COLUMNS (1024px – 1310px) ────────────────────
  // Combines role+status into account cell, merges content+management
  const mediumColumns = [
    {
      title: "Account",
      dataIndex: "name",
      width: "35%",
      render: (name, record) => (
        <div className="user-cell-medium">
          <Avatar
            icon={<UserOutlined />}
            size={36}
            style={{
              backgroundColor: record.isActive ? "#10b981" : "#94a3b8",
              flexShrink: 0,
            }}
          />
          <div className="user-info" style={{ minWidth: 0, flex: 1 }}>
            <Text className="user-name user-name--medium">{name}</Text>
            <Text className="user-email">{record.email}</Text>
            <div className="user-meta-row">
              <Badge
                color={record.role === "admin" ? "#6366f1" : "#64748b"}
                text={
                  <span className="role-text">
                    {record.role === "admin" ? "Admin" : "User"}
                  </span>
                }
              />
              <span className="meta-dot">·</span>
              <StatusTag isActive={record.isActive} />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Recent Activity",
      dataIndex: "lastLoginAt",
      width: "30%",
      render: (date) => {
        const info = formatLastLogin(date);
        if (!info)
          return (
            <Text className="text-muted" style={{ fontSize: 12 }}>
              No activity
            </Text>
          );
        return (
          <div className="login-info">
            <Text className="login-relative">{info.relative}</Text>
            <Text className="login-exact">{info.exact}</Text>
          </div>
        );
      },
    },
    {
      title: "Content",
      align: "center",
      width: "18%",
      render: (_, record) =>
        record.hasPosts ? (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            className="action-link"
            style={{ fontSize: 13 }}
            onClick={() => navigate(`/admin/user-posts/${record._id}`)}
          >
            View
          </Button>
        ) : (
          <Text className="text-muted" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
    },
    {
      title: "Management",
      align: "center",
      width: "17%",
      render: (_, record) => <ToggleButton record={record} />,
    },
  ];

  // ─── MOBILE CARD ─────────────────────────────────────────────────
  const MobileUserCard = ({ record }) => {
    const loginInfo = formatLastLogin(record.lastLoginAt);
    return (
      <div className="mobile-user-card">
        <div className="mobile-card-header">
          <div className="mobile-card-identity">
            <Avatar
              icon={<UserOutlined />}
              size={42}
              style={{
                backgroundColor: record.isActive ? "#10b981" : "#94a3b8",
                flexShrink: 0,
              }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text className="user-name" style={{ maxWidth: "100%", display: "block" }}>
                {record.name}
              </Text>
              <Text className="user-email" style={{ maxWidth: "100%", display: "block" }}>
                {record.email}
              </Text>
            </div>
          </div>
          <StatusTag isActive={record.isActive} />
        </div>

        <div className="mobile-card-details">
          <div className="mobile-card-row">
            <Text className="mobile-card-label">Access Level</Text>
            <Badge
              color={record.role === "admin" ? "#6366f1" : "#64748b"}
              text={record.role === "admin" ? "Administrator" : "Standard User"}
            />
          </div>
          <div className="mobile-card-row">
            <Text className="mobile-card-label">Last Activity</Text>
            {loginInfo ? (
              <div style={{ textAlign: "right" }}>
                <div className="login-relative">{loginInfo.relative}</div>
                <div className="login-exact">{loginInfo.exact}</div>
              </div>
            ) : (
              <Text className="text-muted">No activity</Text>
            )}
          </div>
          <div className="mobile-card-row">
            <Text className="mobile-card-label">Content</Text>
            {record.hasPosts ? (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                className="action-link"
                style={{ padding: 0, height: "auto" }}
                onClick={() => navigate(`/admin/user-posts/${record._id}`)}
              >
                View Content
              </Button>
            ) : (
              <Text className="text-muted">No content</Text>
            )}
          </div>
        </div>

        <div className="mobile-card-footer">
          <ToggleButton record={record} block />
        </div>
      </div>
    );
  };

  const paginationConfig = {
    current: page,
    pageSize: limit,
    total,
    showSizeChanger: false,
    onChange: (p) => setPage(p),
    showTotal: (t) => `Total ${t} registered accounts`,
  };

  return (
    <div className="manage-users">
      {/* ── HEADER ─────────────────────────────────── */}
      <div className="page-header">
        <div className="page-header__left">
          <Title level={4} className="page-title">
            User Administration
          </Title>
          <Text className="page-description">
            Manage user accounts, access permissions, and account status
          </Text>
        </div>
        <div className="page-header__right">
          <div className="stats-cards">
            <div className="stat-card stat-card--active">
              <div className="stat-card__icon">
                <CheckCircleOutlined />
              </div>
              <div className="stat-card__content">
                <Text className="stat-card__label">Active Accounts</Text>
                <Text className="stat-card__value">{counts.active}</Text>
              </div>
            </div>
            <div className="stat-card stat-card--inactive">
              <div className="stat-card__icon">
                <CloseCircleOutlined />
              </div>
              <div className="stat-card__content">
                <Text className="stat-card__label">Suspended</Text>
                <Text className="stat-card__value">{counts.inactive}</Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── FILTERS ────────────────────────────────── */}
      <div className="filters-section">
        <div className="filters-section__search">
          <div className="search-wrapper">
            <span className="search-icon-wrap">
              <SearchOutlined />
            </span>
            <Input
              placeholder="Search by name or email address..."
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="search-input"
              size="middle"
            />
          </div>
        </div>
        <div className="filters-section__controls">
          <Select
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            className="filter-select"
            classNames={{ popup: { root: "filter-dropdown" } }}
          >
            <Option value="all">All Accounts</Option>
            <Option value="active">Active Only</Option>
            <Option value="inactive">Suspended Only</Option>
          </Select>
          <Select
            value={postFilter}
            onChange={(v) => {
              setPostFilter(v);
              setPage(1);
            }}
            className="filter-select"
            classNames={{ popup: { root: "filter-dropdown" } }}
          >
            <Option value="all">All Content</Option>
            <Option value="true">With Posts</Option>
            <Option value="false">No Posts</Option>
          </Select>
        </div>
      </div>

      {/* ── CONTENT ────────────────────────────────── */}
      {useCardLayout ? (
        /* ── TABLET + MOBILE: card layout ── */
        <div className="card-list-container">
          {loading ? (
            <div className="list-state-box">
              <Spin description="Loading users..." />
            </div>
          ) : users.length === 0 ? (
            <div className="list-state-box">
              <Text className="text-muted">No users found</Text>
            </div>
          ) : (
            <>
              <div className={isTablet ? "tablet-grid" : "mobile-list"}>
                {users.map((user) => (
                  <MobileUserCard key={user._id} record={user} />
                ))}
              </div>
              <div className="list-pagination">
                <Text className="list-pagination-total">
                  Total {total} registered accounts
                </Text>
                <Pagination
                  {...paginationConfig}
                  showTotal={undefined}
                  size="small"
                />
              </div>
            </>
          )}
        </div>
      ) : (
        /* ── DESKTOP: table layout ── */
        <Card className="data-table">
          <Table
            rowKey="_id"
            columns={isMediumDesktop ? mediumColumns : fullColumns}
            dataSource={users}
            loading={{ spinning: loading, description: "Loading users..." }}
            locale={{ emptyText: "No users found" }}
            pagination={paginationConfig}
            className="user-table"
            tableLayout="fixed"
          />
        </Card>
      )}
    </div>
  );
}

export default ManageUsers;